<template>
  <!-- El servidor ha rechazado el token (402/401/403): sólo queda ir al login. -->
  <div
    v-if="tokenExpired"
    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
  >
    <div class="bg-white rounded-lg p-6 max-w-md mx-4">
      <div class="flex items-center mb-4">
        <svg class="w-6 h-6 text-red-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.232 16.5c-.77.833.192 2.5 1.732 2.5z"
          ></path>
        </svg>
        <h3 class="text-lg font-semibold text-gray-900">{{ $t("sesion_caducada") }}</h3>
      </div>
      <p class="text-gray-600 mb-6">
        {{ $t("sesion_caducada_mensaje", { seconds: expiredSecondsLeft }) }}
      </p>
      <div class="flex justify-end">
        <button
          @click="closeExpiredNotice"
          class="px-4 py-2 bg-black text-white rounded hover:bg-gray-800"
        >
          {{ $t("ir_al_login") }}
        </button>
      </div>
    </div>
  </div>
  <div
    v-else-if="showAlert"
    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
  >
    <div class="bg-white rounded-lg p-6 max-w-md mx-4">
      <div class="flex items-center mb-4">
        <svg
          class="w-6 h-6 text-orange-500 mr-3"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.232 16.5c-.77.833.192 2.5 1.732 2.5z"
          ></path>
        </svg>
        <h3 class="text-lg font-semibold text-gray-900">{{ $t("sesion_por_expirar") }}</h3>
      </div>

      <p class="text-gray-600 mb-6">
        {{ $t("tu_sesion_expirara_en_segundos", { seconds: timeRemaining }) }}
      </p>

      <div class="flex justify-end space-x-3">
        <button
          @click="logout"
          class="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
        >
          {{ $t("cerrar_sesion") }}
        </button>
        <!-- "Sigo aquí" sólo reinicia el contador: el token lo renueva solo
             sessionService con el refresh token. -->
        <button
          @click="renewSession"
          class="px-4 py-2 bg-black text-white rounded hover:bg-gray-800"
        >
          {{ $t("sigo_aqui") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, watch } from "vue";
import { useAuthStore } from "@/stores/auth";
import sessionService from "@/services/session";

defineOptions({ name: "SessionExpiryAlert" });

// Modelo:
//   - `sessionService.sessionDuration` = tiempo de inactividad permitido
//     antes de avisar. Mientras el usuario interactúa, App.vue refresca
//     `lastActivity` y `msUntilExpiry()` se mantiene en `sessionDuration`.
//   - En cuanto se cumple la `sessionDuration` sin actividad, aparece el
//     aviso con un grace period (`sessionService.inactivityGraceMs`) para que el
//     usuario decida renovar o cerrar. Mientras el aviso está visible,
//     `sessionService` no fuerza el logout (cede el control al modal).
//   - Si el grace se agota sin acción del usuario → endSession("timeout").
//   - El token no caduca la sesión: sessionService lo renueva con el refresh
//     token. Si el servidor lo rechaza sin remedio, se muestra el aviso de
//     sesión caducada (notifyTokenExpired) y se va al login.
const EXPIRED_NOTICE_MS = 10 * 1000;
const POLL_INTERVAL_MS = 1000;

const authStore = useAuthStore();

const showAlert = ref(false);
const timeRemaining = ref(0);
// Marca cuándo apareció el aviso por primera vez en este ciclo de
// inactividad. Se usa para calcular cuánto grace queda; lo reseteamos al
// renovar, al ocultarse el aviso o al desmontarse el componente.
const alertShownAt = ref<number | null>(null);
let pollTimer: ReturnType<typeof setInterval> | null = null;

const tokenExpired = ref(false);
const expiredSecondsLeft = ref(0);
let expiredTimer: ReturnType<typeof setInterval> | null = null;
let resolveExpiredNotice: (() => void) | null = null;

function msUntilExpiry(): number {
  return sessionService.sessionDuration - (Date.now() - sessionService.ultimaActividad(authStore));
}

function tick() {
  if (!authStore.token) {
    stopPolling();
    return;
  }
  const remaining = msUntilExpiry();

  if (remaining > 0) {
    // Sesión aún viva: aviso oculto y se resetea el ciclo de grace.
    hideAlert();
    return;
  }

  // Sesión expirada: el aviso pasa a contar su propio grace period.
  if (alertShownAt.value === null) {
    alertShownAt.value = Date.now();
  }
  const graceLeft = sessionService.inactivityGraceMs - (Date.now() - alertShownAt.value);

  if (graceLeft <= 0) {
    stopPolling();
    void sessionService.endSession("timeout");
    return;
  }

  showAlert.value = true;
  timeRemaining.value = Math.ceil(graceLeft / 1000);
}

function startPolling() {
  stopPolling();
  pollTimer = setInterval(tick, POLL_INTERVAL_MS);
  tick();
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  hideAlert();
  // El watcher de showAlert lo haría también, pero en unmount el efecto
  // puede no llegar a dispararse — lo limpiamos a mano para asegurar que
  // no quede el flag puesto entre montajes.
  sessionService.warningActive = false;
}

function hideAlert() {
  showAlert.value = false;
  alertShownAt.value = null;
}

async function renewSession() {
  sessionService.warningActive = false;
  await sessionService.resetTimer();
  hideAlert();
}

// Aviso de token rechazado por el servidor. Lo invoca
// sessionService.handleTokenExpiry(), que espera a que se resuelva (botón o
// fin de la cuenta atrás) para cerrar la sesión e ir al login.
function notifyTokenExpired(): Promise<void> {
  // El aviso de inactividad ya no tiene sentido.
  stopPolling();
  const expiresAt = Date.now() + EXPIRED_NOTICE_MS;
  const updateCountdown = () => {
    expiredSecondsLeft.value = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
    if (expiredSecondsLeft.value === 0) closeExpiredNotice();
  };
  tokenExpired.value = true;
  return new Promise((resolve) => {
    resolveExpiredNotice = resolve;
    updateCountdown();
    expiredTimer = setInterval(updateCountdown, POLL_INTERVAL_MS);
  });
}

function closeExpiredNotice() {
  if (expiredTimer) {
    clearInterval(expiredTimer);
    expiredTimer = null;
  }
  tokenExpired.value = false;
  resolveExpiredNotice?.();
  resolveExpiredNotice = null;
}

async function logout() {
  stopPolling();
  await sessionService.endSession("user_logout");
}

// Mientras el modal está visible, le decimos al sessionService que ignore
// los eventos de actividad — si no, mover el ratón sobre la alerta haría
// que `resetTimer` la cerrara antes de que el usuario pueda interactuar.
watch(showAlert, (visible) => {
  sessionService.warningActive = visible;
});

watch(
  () => authStore.token,
  (token) => {
    if (token) {
      startPolling();
    } else {
      stopPolling();
      closeExpiredNotice();
    }
  },
);

onMounted(() => {
  sessionService.setTokenExpiredNotifier(notifyTokenExpired);
  if (authStore.token) startPolling();
});

onBeforeUnmount(() => {
  sessionService.setTokenExpiredNotifier(null);
  closeExpiredNotice();
  stopPolling();
});
</script>

<style scoped>
/* Los estilos están inline en las clases de Tailwind */
</style>
